import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import DataFreshnessBadge from "../components/DataFreshnessBadge";
import AIDisclaimer from "../components/AIDisclaimer";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

function KPIValue({ pack, unit }) {
  if (!pack) return <span className="text-slate-400">--</span>;
  if (pack.status === "data_missing")
    return (
      <span data-testid="kpi-missing" className="text-red-600">
        Date indisponibile
      </span>
    );
  if (typeof pack.value === "number")
    return <span>{pack.value.toLocaleString("ro-RO")}{unit ? ` ${unit}` : ""}</span>;
  return <span className="text-slate-400">--</span>;
}

function SourceFootnote({ source }) {
  if (!source) return null;
  return (
    <p className="mt-2 text-[11px] text-slate-400">
      {source.database}.{source.collection}
    </p>
  );
}

const GROUPS = [
  {
    key: "an_2026",
    title: "Anul 2026",
    fields: [
      ["leads_crm_2026", "Lead-uri CRM introduse in 2026"],
      ["distribuitori_2026", "Utilizatori Configurator (Distribuitori) adaugati in 2026"],
      ["clienti_erp_2026", "Clienti ERP adaugati in 2026"],
      ["comenzi_validate_fin_2026", "Comenzi validate financiar in 2026"],
      ["valoare_validate_fin_2026", "Valoare comenzi validate financiar 2026 (fara TVA)", "EUR"],
      ["comenzi_fara_val_fin_2026", "Comenzi fara validare FIN adaugate in 2026"],
      ["valoare_fara_val_fin_2026", "Valoare comenzi fara validare FIN 2026 (fara TVA)", "EUR"],
    ],
  },
  {
    key: "marketing_2026",
    title: "Marketing 2026",
    fields: [
      ["mailuri_trimise_2026", "Mail-uri trimise in toate campaniile 2026"],
      ["clicuri_2026", "Clicuri in toate campaniile 2026"],
      ["campanii_trimise_2026", "Trimise campanii.amel.ro (toti providerii)"],
      ["campanii_clicuri_2026", "Clicuri campanii.amel.ro (toti providerii)"],
    ],
  },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: resp } = await api.get("/api/datahub/snapshot");
      setData(resp);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Nu am putut incarca snapshot-ul");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await api.post("/api/datahub/refresh");
      await load();
      toast.success("Snapshot regenerat");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Refresh esuat");
    } finally {
      setRefreshing(false);
    }
  };

  const snap = data?.snapshot;
  const freshness = data?.freshness;
  const storedAt = freshness?.stored_at || snap?.generated_at || null;
  const ttl = freshness?.ttl_seconds || 900;

  return (
    <Layout title="Vedere de ansamblu">
      <section className="mb-8">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-400">Sesiune 2 - DataHub</p>
            <h2 data-testid="dashboard-greeting" className="text-2xl font-semibold">
              Buna, {user?.name || "utilizator"}.
            </h2>
            <p className="mt-1 text-sm text-slate-500 max-w-2xl">
              Snapshot agregat din cele trei surse. Toate cifrele au sursa atasata
              (baza.colectie). Conexiunile catre surse sunt read-only.
            </p>
          </div>
          <DataFreshnessBadge timestamp={storedAt} ttlSeconds={ttl} onRefresh={onRefresh} />
        </div>
      </section>

      {loading ? (
        <div data-testid="dashboard-loading" className="text-slate-500">Incarc...</div>
      ) : (
        <div className="space-y-8">
          {GROUPS.map((group, idx) => (
            <section
              key={group.key}
              className={idx > 0 ? "border-t border-slate-300 pt-8" : undefined}
            >
              <h3 className="mb-3 text-sm uppercase tracking-widest text-slate-500">{group.title}</h3>
              <div data-testid={`group-${group.key}`} className="grid grid-cols-1 gap-4 md:grid-cols-3">
                {group.fields.map(([fkey, label, unit]) => {
                  const pack = snap?.kpis?.[group.key]?.[fkey];
                  return (
                    <article
                      key={fkey}
                      data-testid={`kpi-${group.key}-${fkey}`}
                      className="rounded-lg border border-slate-200 bg-white p-4"
                    >
                      <div className="text-xs uppercase tracking-widest text-slate-400">{label}</div>
                      <div className="mt-3 text-2xl font-semibold">
                        <KPIValue pack={pack} unit={unit} />
                      </div>
                      <SourceFootnote source={pack?.source} />
                    </article>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}

      <AIDisclaimer>
        Cifrele nu sunt generate de AI. Sunt counts reali din bazele sursa. AI Board
        si recomandari automate apar in sesiunile urmatoare.
      </AIDisclaimer>

      {refreshing ? <div className="mt-3 text-xs text-slate-500">Reincarc...</div> : null}
    </Layout>
  );
}
